<template>
    <div>
        <!-- Header section -->
        <header class="header-section-team header-section">
            <div class="container text-right">
                <div class="page-info">
                    <h2>{{ meta.title }}</h2>
                    <div class="page-links">
                        <router-link to="/">Начало</router-link>
                        <span class="active">Цени</span>
                    </div>
                </div>
            </div>
        </header>
        <!-- Header section end -->
        <section class="container price-wrapper">
            <div class="row justify-content-center">
                <div class="col-lg-8 col-md-10 text-center">
                    <h1 class="price-heading">{{ meta.heading }}</h1>
                    <p v-if="meta.subtitle" class="price-subtitle text-muted">{{ meta.subtitle }}</p>
                    <button
                            v-if="canEdit"
                            class="btn btn-outline-secondary btn-sm mt-3"
                            type="button"
                            :disabled="!sections.length"
                            @click="downloadCsv"
                    >
                        Свали CSV
                    </button>
                </div>
            </div>

            <div
                    v-for="(section, index) in sections"
                    :key="`section-${section.id || index}`"
                    class="row justify-content-center price-section"
            >
                <div class="col-lg-8 col-md-10">
                    <h3 class="section-title text-center">{{ section.title }}</h3>
                    <div class="d-flex justify-content-end mb-2" v-if="canEdit">
                        <button
                                class="btn btn-sm btn-outline-primary"
                                type="button"
                                @click="openEditor(section.id)"
                        >
                            + Добави услуга
                        </button>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-borderless price-table">
                            <tbody>
                            <tr
                                    v-for="(item, itemIndex) in section.items"
                                    :key="`item-${section.id}-${itemIndex}`"
                                    :class="['price-row', { 'price-row--title': isTitleRow(item), 'price-row--new': canEdit && isNewlyAdded(section.id, item) }]"
                            >
                                <td class="align-middle">
                                    <div>{{ item.name }}</div>
                                    <div v-if="item.body" class="price-body">{{ item.body }}</div>
                                </td>
                                <td class="text-right align-middle price-value-cell">
                                    <span v-if="!isTitleRow(item)" v-html="formatPrice(item.price)"></span>
                                    <button
                                            v-if="canEdit && !isTitleRow(item)"
                                            class="btn btn-link price-edit-icon"
                                            type="button"
                                            @click="openEditor(section.id, item)"
                                            aria-label="Редактирай"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                            v-if="canEdit && !isTitleRow(item)"
                                            class="btn btn-link price-delete-icon"
                                            type="button"
                                            @click="openDeleteConfirm(section.id, item)"
                                            aria-label="Изтрий"
                                    >
                                        ✖️
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="loadError" class="row justify-content-center">
                <div class="col-lg-10 text-center text-danger">
                    {{ loadError }}
                </div>
            </div>
            <div v-else-if="!sections.length" class="row justify-content-center">
                <div class="col-lg-10 text-center text-muted">
                    Няма налична ценова информация. Моля, проверете отново по-късно.
                </div>
            </div>
        </section>

        <div v-if="showEditor" class="price-modal">
            <div class="price-modal__dialog">
                <h5 class="mb-2">{{ editingTarget && editingTarget.isNew ? 'Добавяне на услуга' : 'Редакция на услуга' }}</h5>
                <div class="form-group">
                    <label>Име</label>
                    <input
                            v-model.trim="editorName"
                            type="text"
                            class="form-control"
                            required
                    >
                </div>
                <div class="form-group">
                    <label>Бележки</label>
                    <textarea
                            v-model.trim="editorBody"
                            class="form-control"
                            rows="2"
                    ></textarea>
                </div>
                <div class="form-group">
                    <label>Тип цена</label>
                    <select class="form-control" v-model="editorPriceMode">
                        <option value="single">Единична цена</option>
                        <option value="range">Диапазон</option>
                    </select>
                </div>
                <div class="form-group" v-if="editorPriceMode === 'single'">
                    <label>Цена (€)</label>
                    <input
                            v-model.trim="editorPrice"
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control"
                            required
                    >
                </div>
                <div class="form-row" v-else>
                    <div class="form-group col-md-6">
                        <label>Минимална цена (€)</label>
                        <input
                                v-model.trim="editorPriceMin"
                                type="number"
                                step="0.01"
                                min="0"
                                class="form-control"
                                required
                        >
                    </div>
                    <div class="form-group col-md-6">
                        <label>Максимална цена (€)</label>
                        <input
                                v-model.trim="editorPriceMax"
                                type="number"
                                step="0.01"
                                min="0"
                                class="form-control"
                                required
                        >
                    </div>
                </div>
                <div class="d-flex justify-content-end align-items-center">
                    <button class="btn btn-link mr-3" type="button" :disabled="isSaving" @click="closeEditor">
                        Откажи
                    </button>
                    <button class="btn btn-primary" type="button" :disabled="isSaving" @click="savePrice">
                        {{ isSaving ? 'Запазване...' : 'Запази' }}
                    </button>
                </div>
            </div>
        </div>
        <div v-if="showDeleteConfirm" class="price-modal">
            <div class="price-modal__dialog">
                <h5 class="mb-3">Потвърдете изтриването</h5>
                <p class="text-muted">Сигурни ли сте, че искате да премахнете „{{ deleteTarget?.name }}“?</p>
                <div class="d-flex justify-content-end">
                    <button class="btn btn-link mr-3" type="button" @click="closeDeleteConfirm">Откажи</button>
                    <button class="btn btn-danger" type="button" @click="confirmRemoval" :disabled="isSaving">Изтрий</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { db } from '../../firebase';
    import { mapGetters } from 'vuex';

    const DEFAULT_META = {
        title: 'Цени',
        heading: 'Ценоразпис',
        subtitle: '/съгласуван с Ветеринарномедицинска Колегия София-град/'
    };

    const BGN_TO_EUR = 1.95583;

    export default {
        data() {
            return {
                meta: { ...DEFAULT_META },
                sections: [],
                unsubscribe: null,
                showEditor: false,
                showDeleteConfirm: false,
                editingTarget: null,
                deleteTarget: null,
                editorPrice: '',
                editorPriceMode: 'single',
                editorPriceMin: '',
                editorPriceMax: '',
                editorName: '',
                editorBody: '',
                isSaving: false,
                loadError: null,
                recentlyAdded: []
            }
        },
        computed: {
            ...mapGetters(['user']),
            canEdit() {
                return !!this.user;
            }
        },
        created() {
            this.unsubscribe = db.collection('prices')
                .orderBy('order')
                .onSnapshot(
                    (snapshot) => {
                        console.log('[Prices] Snapshot size:', snapshot.size);
                        const sections = [];
                        snapshot.forEach((doc) => {
                            const data = doc.data();
                            console.log('[Prices] Doc loaded:', doc.id, data.title);
                            sections.push({
                                title: data.title || data.name || '',
                                id: doc.id,
                                rawServices: data.services,
                                items: this.normalizeServices(data.services)
                            });
                        });
                        this.sections = sections;
                        this.loadError = null;
                    },
                    (error) => {
                        console.error('[Prices] Firestore listener error:', error);
                        this.loadError = 'Грешка при зареждане на цените. Проверете правата си за достъп.';
                    }
                );
        },
        beforeDestroy() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        },
        methods: {
            isTitleRow(item) {
                return item.price === null || item.price === undefined || item.price === '';
            },
            normalizeServices(servicesField) {
                if (Array.isArray(servicesField)) {
                    return servicesField.map((item, index) => ({
                        key: index,
                        name: item.name || '',
                        body: item.body || '',
                        price: item.price
                    }));
                }

                if (servicesField && typeof servicesField === 'object') {
                    return Object.keys(servicesField)
                        .sort((a, b) => Number(a) - Number(b))
                        .map((key) => ({
                            key,
                            name: servicesField[key]?.name || '',
                            body: servicesField[key]?.body || '',
                            price: servicesField[key]?.price
                        }));
                }

                return [];
            },
            formatPrice(value) {
                const parsed = this.parsePriceValue(value);
                if (parsed.type === 'range') {
                    if (parsed.min !== null && parsed.max !== null) {
                        const minEur = this.formatNumber(parsed.min / BGN_TO_EUR, 2);
                        const maxEur = this.formatNumber(parsed.max / BGN_TO_EUR, 2);
                        return `${minEur}€ - ${maxEur}€ / ${this.formatNumber(parsed.min)} - ${this.formatNumber(parsed.max)} лв`;
                    }
                    if (parsed.min !== null) {
                        return this.composePriceString(parsed.min);
                    }
                    if (parsed.max !== null) {
                        return this.composePriceString(parsed.max);
                    }
                }
                if (parsed.type === 'single' && parsed.value !== null) {
                    return this.composePriceString(parsed.value);
                }
                if (parsed.type === 'text') {
                    return parsed.raw;
                }
                return '';
            },
            composePriceString(amount) {
                const lvFormatted = this.formatNumber(amount);
                const eurFormatted = this.formatNumber(amount / BGN_TO_EUR, 2);
                return `${eurFormatted}€ / ${lvFormatted} лв`;
            },
            formatNumber(value, decimals) {
                const precision = typeof decimals === 'number'
                    ? decimals
                    : (Number.isInteger(value) ? 0 : 2);
                return Number(value).toFixed(precision);
            },
            downloadCsv() {
                const rows = [
                    ['Секция', 'Манипулация', 'Бележки', 'Цена (лв)', 'Цена (€)']
                ];

                this.sections.forEach((section) => {
                    section.items.forEach((item) => {
                        if (this.isTitleRow(item)) {
                            rows.push([section.title, item.name, '', '', '']);
                            return;
                        }

                        const parsedPrice = this.parsePriceValue(item.price);
                        let lvValue = '';
                        let eurValue = '';

                        if (parsedPrice.type === 'range' && parsedPrice.min !== null && parsedPrice.max !== null) {
                            lvValue = `${this.formatNumber(parsedPrice.min)} - ${this.formatNumber(parsedPrice.max)}`;
                            eurValue = `${this.formatNumber(parsedPrice.min / BGN_TO_EUR, 2)} - ${this.formatNumber(parsedPrice.max / BGN_TO_EUR, 2)}`;
                        } else if (parsedPrice.type === 'range' && parsedPrice.min !== null) {
                            lvValue = this.formatNumber(parsedPrice.min);
                            eurValue = this.formatNumber(parsedPrice.min / BGN_TO_EUR, 2);
                        } else if (parsedPrice.type === 'range' && parsedPrice.max !== null) {
                            lvValue = this.formatNumber(parsedPrice.max);
                            eurValue = this.formatNumber(parsedPrice.max / BGN_TO_EUR, 2);
                        } else if (parsedPrice.type === 'single' && parsedPrice.value !== null) {
                            lvValue = this.formatNumber(parsedPrice.value);
                            eurValue = this.formatNumber(parsedPrice.value / BGN_TO_EUR, 2);
                        } else if (parsedPrice.type === 'text') {
                            lvValue = parsedPrice.raw;
                        }

                        rows.push([
                            section.title,
                            item.name,
                            item.body || '',
                            lvValue,
                            eurValue
                        ]);
                    });
                });

                const csvContent = rows
                    .map((row) => row.map((value) => this.escapeCsv(value)).join(','))
                    .join('\n');

                const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'prices.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            },
            openEditor(sectionId, item) {
                if (item && this.isTitleRow(item)) {
                    return;
                }
                const currentUser = this.$store.getters.user;
                console.log('[Prices] Current user from store:', currentUser);
                const parsedPrice = this.parsePriceValue(item ? item.price : null);
                this.editingTarget = {
                    sectionId,
                    key: item ? item.key : null,
                    isNew: !item
                };
                this.editorName = item ? item.name : '';
                this.editorBody = item ? (item.body || '') : '';
                if (parsedPrice.type === 'range') {
                    this.editorPriceMode = 'range';
                    this.editorPriceMin = parsedPrice.min !== null ? this.formatNumber(parsedPrice.min / BGN_TO_EUR, 2) : '';
                    this.editorPriceMax = parsedPrice.max !== null ? this.formatNumber(parsedPrice.max / BGN_TO_EUR, 2) : '';
                    this.editorPrice = '';
                } else {
                    this.editorPriceMode = 'single';
                    this.editorPrice = parsedPrice.type === 'single' && parsedPrice.value !== null
                        ? this.formatNumber(parsedPrice.value / BGN_TO_EUR, 2)
                        : '';
                    this.editorPriceMin = '';
                    this.editorPriceMax = '';
                }
                this.showEditor = true;
            },
            closeEditor() {
                this.editingTarget = null;
                this.editorPrice = '';
                this.editorPriceMode = 'single';
                this.editorPriceMin = '';
                this.editorPriceMax = '';
                this.editorName = '';
                this.editorBody = '';
                this.showEditor = false;
            },
            savePrice() {
                if (!this.editingTarget) {
                    console.warn('[Prices] savePrice called without editingTarget');
                    return;
                }
                const currentUser = this.$store.getters.user;
                console.log('[Prices] savePrice user id:', currentUser);
                const cleanPrice = this.editorPrice === null || this.editorPrice === undefined
                    ? ''
                    : this.editorPrice.toString().trim();
                const cleanName = this.editorName === null || this.editorName === undefined
                    ? ''
                    : this.editorName.toString().trim();
                if (!cleanName) {
                    console.warn('[Prices] Empty name provided');
                    return;
                }
                if (this.editorPriceMode === 'single' && !cleanPrice) {
                    console.warn('[Prices] Empty price provided');
                    return;
                }
                let servicePayload;
                if (this.editorPriceMode === 'range') {
                    const minVal = this.editorPriceMin === null || this.editorPriceMin === undefined
                        ? ''
                        : this.editorPriceMin.toString().trim();
                    const maxVal = this.editorPriceMax === null || this.editorPriceMax === undefined
                        ? ''
                        : this.editorPriceMax.toString().trim();
                    if (!minVal || !maxVal) {
                        console.warn('[Prices] Range requires both values');
                        return;
                    }
                    servicePayload = this.prepareServicePayload({
                        mode: 'range',
                        priceRange: {
                            min: minVal,
                            max: maxVal
                        },
                        name: cleanName,
                        body: this.editorBody
                    });
                } else {
                    servicePayload = this.prepareServicePayload({
                        mode: 'single',
                        price: cleanPrice,
                        name: cleanName,
                        body: this.editorBody
                    });
                }
                const targetSnapshot = {
                    isNew: !!this.editingTarget.isNew,
                    sectionId: this.editingTarget.sectionId
                };
                const section = this.sections.find((sec) => sec.id === targetSnapshot.sectionId);
                if (!section) {
                    console.warn('[Prices] Section not found', this.editingTarget.sectionId);
                    return;
                }
                const { services: updatedServices, newKey } = this.buildUpdatedServices(section.rawServices, this.editingTarget, servicePayload);
                console.log('[Prices] Updating document', section.id, 'entry', this.editingTarget.key, 'with data', servicePayload);
                this.isSaving = true;
                db.collection('prices').doc(section.id)
                    .update({ services: updatedServices })
                    .then(() => {
                        console.log('[Prices] Update success');
                        this.closeEditor();
                        this.loadError = null;
                        if (targetSnapshot.isNew && newKey !== null) {
                            this.recentlyAdded.push({
                                sectionId: targetSnapshot.sectionId,
                                key: newKey
                            });
                        }
                    }).catch((error) => {
                        console.error('[Prices] Update failed:', error);
                        this.loadError = 'Нямате права за редакция на тази цена.';
                    }).finally(() => {
                        this.isSaving = false;
                    });
            },
            buildUpdatedServices(originalServices, target, servicePayload) {
                if (Array.isArray(originalServices)) {
                    if (target.isNew) {
                            const services = [...originalServices, servicePayload];
                            return { services, newKey: String(services.length - 1) };
                    }
                    return {
                        services: originalServices.map((item, index) => {
                        if (index === Number(target.key)) {
                            return {
                                ...item,
                                ...servicePayload
                            };
                        }
                        return item;
                        }),
                        newKey: null
                    };
                }

                if (originalServices && typeof originalServices === 'object') {
                    if (target.isNew) {
                        const newKey = this.generateObjectKey(originalServices);
                        return {
                            services: {
                                ...originalServices,
                                [newKey]: servicePayload
                            },
                            newKey
                        };
                    }
                    return {
                        services: {
                            ...originalServices,
                            [target.key]: {
                                ...(originalServices[target.key] || {}),
                                ...servicePayload
                            }
                        },
                        newKey: null
                    };
                }

                return { services: originalServices, newKey: null };
            },
            prepareServicePayload({ mode, price, priceRange, name, body }) {
                if (mode === 'range') {
                    const minBgn = this.convertEuroToBgn(priceRange.min);
                    const maxBgn = this.convertEuroToBgn(priceRange.max);
                    if (minBgn === null || maxBgn === null) {
                        return {
                            name,
                            body: body || '',
                            price: priceRange
                        };
                    }
                    const normalizedMin = Math.min(minBgn, maxBgn);
                    const normalizedMax = Math.max(minBgn, maxBgn);
                    return {
                        name,
                        body: body || '',
                        price: {
                            min: normalizedMin,
                            max: normalizedMax
                        }
                    };
                }
                const singleBgn = this.convertEuroToBgn(price);
                return {
                    name,
                    body: body || '',
                    price: singleBgn === null ? price : singleBgn
                };
            },
            convertEuroToBgn(value) {
                if (value === null || value === undefined || value === '') {
                    return null;
                }
                const numeric = parseFloat(String(value).replace(',', '.'));
                if (isNaN(numeric)) {
                    return null;
                }
                return Number((numeric * BGN_TO_EUR).toFixed(2));
            },
            parsePriceValue(value) {
                const toNumber = (input) => {
                    if (typeof input === 'number') {
                        return input;
                    }
                    if (typeof input === 'string') {
                        const parsed = parseFloat(input.replace(',', '.'));
                        return isNaN(parsed) ? null : parsed;
                    }
                    return null;
                };

                if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
                    return {
                        type: 'range',
                        min: toNumber(value.min),
                        max: toNumber(value.max)
                    };
                }

                if (typeof value === 'number') {
                    return {
                        type: 'single',
                        value
                    };
                }

                if (typeof value === 'string') {
                    const euroRangeMatch = value.match(/([0-9.,]+)\s*[-–]\s*([0-9.,]+)\s*€/);
                    if (euroRangeMatch) {
                        const min = parseFloat(euroRangeMatch[1].replace(',', '.'));
                        const max = parseFloat(euroRangeMatch[2].replace(',', '.'));
                        if (!isNaN(min) || !isNaN(max)) {
                            return {
                                type: 'range',
                                min: isNaN(min) ? null : Number((min * BGN_TO_EUR).toFixed(2)),
                                max: isNaN(max) ? null : Number((max * BGN_TO_EUR).toFixed(2))
                            };
                        }
                    }

                    const lvRangeMatch = value.match(/([0-9.,]+)\s*[-–]\s*([0-9.,]+)\s*лв/);
                    if (lvRangeMatch) {
                        const min = parseFloat(lvRangeMatch[1].replace(',', '.'));
                        const max = parseFloat(lvRangeMatch[2].replace(',', '.'));
                        if (!isNaN(min) || !isNaN(max)) {
                            return {
                                type: 'range',
                                min: isNaN(min) ? null : min,
                                max: isNaN(max) ? null : max
                            };
                        }
                    }

                    const euroSingleMatch = value.match(/([0-9.,]+)\s*€/);
                    if (euroSingleMatch) {
                        const amt = parseFloat(euroSingleMatch[1].replace(',', '.'));
                        if (!isNaN(amt)) {
                            return {
                                type: 'single',
                                value: Number((amt * BGN_TO_EUR).toFixed(2))
                            };
                        }
                    }

                    const numeric = parseFloat(value.replace(',', '.'));
                    if (!isNaN(numeric)) {
                        return {
                            type: 'single',
                            value: numeric
                        };
                    }

                    return {
                        type: 'text',
                        raw: value
                    };
                }

                return {
                    type: 'single',
                    value: null
                };
            },
            generateObjectKey(services) {
                const numericKeys = Object.keys(services)
                    .map((key) => Number(key))
                    .filter((value) => !isNaN(value));
                if (!numericKeys.length) {
                    return '0';
                }
                return String(Math.max(...numericKeys) + 1);
            },
            openDeleteConfirm(sectionId, item) {
                this.deleteTarget = {
                    sectionId,
                    key: item.key,
                    name: item.name
                };
                this.showDeleteConfirm = true;
            },
            closeDeleteConfirm() {
                this.deleteTarget = null;
                this.showDeleteConfirm = false;
            },
            confirmRemoval() {
                if (!this.deleteTarget) {
                    return;
                }
                this.removeItem(this.deleteTarget.sectionId, this.deleteTarget);
            },
            removeItem(sectionId, item) {
                if (!this.canEdit) {
                    return;
                }
                const section = this.sections.find((sec) => sec.id === sectionId);
                if (!section) {
                    return;
                }
                const updatedServices = this.performRemoval(section.rawServices, item.key);
                if (!updatedServices) {
                    return;
                }
                db.collection('prices').doc(section.id).update({
                    services: updatedServices
                }).then(() => {
                    console.log('[Prices] Removed entry', item.key);
                    if (this.showEditor) {
                        this.closeEditor();
                    }
                    this.closeDeleteConfirm();
                    this.removeHighlight(section.id, item.key);
                }).catch((error) => {
                    console.error('[Prices] Remove failed:', error);
                });
            },
            performRemoval(originalServices, key) {
                if (Array.isArray(originalServices)) {
                    return originalServices.filter((_, index) => index !== Number(key));
                }
                if (originalServices && typeof originalServices === 'object') {
                    const updated = { ...originalServices };
                    delete updated[key];
                    return updated;
                }
                return null;
            },
            escapeCsv(value) {
                if (value === null || value === undefined) {
                    return '';
                }
                const stringValue = String(value);
                if (/["\n,]/.test(stringValue)) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            },
            isNewlyAdded(sectionId, item) {
                if (!item || item.key === undefined || item.key === null) {
                    return false;
                }
                return this.recentlyAdded.some((entry) => entry.sectionId === sectionId && String(entry.key) === String(item.key));
            },
            removeHighlight(sectionId, key) {
                this.recentlyAdded = this.recentlyAdded.filter((entry) => !(entry.sectionId === sectionId && String(entry.key) === String(key)));
            }
        }
    }
</script>

<style scoped>
    .price-wrapper {
        padding-top: 60px;
        padding-bottom: 40px;
    }

    .price-heading {
        font-size: 2.25rem;
        margin-bottom: 0.5rem;
    }

    .price-subtitle {
        font-size: 1rem;
    }

    .price-section {
        margin-top: 40px;
    }

    .section-title {
        font-size: 1.4rem;
        margin-bottom: 25px;
        font-weight: 400;
        color: inherit;
    }

    .price-table td {
        padding: 0.9rem 0.75rem;
        border-bottom: 1px solid #f1f1f1;
    }

    .price-body {
        font-size: 0.85rem;
        font-style: italic;
    }

    .price-row--title td {
        border-bottom: none;
        background: transparent;
        font-weight: 400;
    }

    .price-row--new td {
        animation: highlightFade 2s ease-out;
        background: #d4edda;
    }

    .price-row:last-child td {
        border-bottom: none;
    }

    .price-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1050;
    }

    .price-modal__dialog {
        background: #fff;
        padding: 1.5rem;
        width: 100%;
        max-width: 420px;
        border-radius: 4px;
        box-shadow: 0 10px 35px rgba(0,0,0,0.2);
    }

    .price-value-cell {
        white-space: nowrap;
    }

    .price-edit-icon {
        font-size: 0.9rem;
        margin-left: 0.5rem;
        padding: 0;
        vertical-align: middle;
    }

    .price-delete-icon {
        font-size: 0.9rem;
        margin-left: 0.25rem;
        padding: 0;
        color: #c82333;
    }

    @keyframes highlightFade {
        from {
            background: #b7e1c0;
        }
        to {
            background: #d4edda;
        }
    }
</style>
